import { useCallback, useContext, useEffect, useState } from 'react';
import MainContext from '../../context/MainContext';
import useIsMobile from '../../hooks/useIsMobile';
import useToggle from '../../hooks/useToggle';
import { toIcons } from '../../consts/Paths';
import { useForm } from 'react-hook-form';
import Loader from '../Loader';
import CustomModal from '../modals/CustomModal';
import CustomInput from '../inputs/CustomInput';
import useVendors from '../../api/hooks/useVendors';
import useShippingRules from '../../api/hooks/useShippingRules';
import { logMsgBgColors } from '../../consts/misc';

export interface VendorItem {
    id: number;
    name: string;
    code?: string | null;
    shipping_rules?: { id: number; name: string }[];
    shippingRules?: { id: number; name: string }[];
}

const Vendors = (props: any) => {
    const { isMobile } = useIsMobile();
    const { isToggled } = props.resetList;
    const { write } = props.logMessage;
    const { user, userTreated } = useContext(MainContext);

    const { RESPONSES, RESULTS, CTAs } = useVendors();
    const { RESPONSES: RULES_RESPONSES, RESULTS: RULES_RESULTS, CTAs: RULES_CTAs } = useShippingRules();

    const [vendors, setVendors] = useState<VendorItem[]>([]);
    const [shippingRules, setShippingRules] = useState<{ id: number; name: string }[]>([]);
    const [edit, setEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [item, setItem] = useState<VendorItem | null>(null);
    const [selectedRuleIds, setSelectedRuleIds] = useState<number[]>([]);

    const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

    const handelOnDeleteClick = useCallback(
        (id: number) => {
            CTAs.deleteVendor(id);
        },
        [CTAs.deleteVendor]
    );

    useEffect(() => {
        CTAs.getVendors();
    }, [isToggled, user, userTreated, RESPONSES.deleteVendorResponse, RESPONSES.updateVendorResponse, RESPONSES.createVendorResponse]);

    useEffect(() => {
        RULES_CTAs.getShippingRules();
    }, []);

    useEffect(() => {
        if (RESPONSES.getVendorsResponse?.ok && RESULTS.getVendorsResult) {
            const list = Array.isArray(RESULTS.getVendorsResult)
                ? RESULTS.getVendorsResult
                : [];
            setVendors(
                list.map((v: any) => ({
                    id: v.id,
                    name: v.name,
                    code: v.code ?? null,
                    shipping_rules: v.shipping_rules ?? v.shippingRules ?? [],
                }))
            );
        }
    }, [RESPONSES.getVendorsResponse, RESULTS.getVendorsResult]);

    useEffect(() => {
        if (RULES_RESPONSES.getShippingRulesResponse?.ok && RULES_RESULTS.getShippingRulesResult) {
            setShippingRules(
                (RULES_RESULTS.getShippingRulesResult || []).map((r: any) => ({
                    id: r.id,
                    name: r.name,
                }))
            );
        }
    }, [RULES_RESPONSES.getShippingRulesResponse, RULES_RESULTS.getShippingRulesResult]);

    const openEdit = (data: VendorItem) => {
        setItem(data);
        const rules = data.shipping_rules ?? data.shippingRules ?? [];
        setSelectedRuleIds(rules.map((r: any) => r.id));
        setValue('name', data.name);
        setValue('code', data.code ?? '');
        setEdit(true);
    };

    const toggleRule = (ruleId: number) => {
        setSelectedRuleIds((prev) =>
            prev.includes(ruleId) ? prev.filter((id) => id !== ruleId) : [...prev, ruleId]
        );
    };

    const onSubmit = (data: any) => {
        if (!item) return;
        setIsLoading(true);
        CTAs.updateVendor(item.id, {
            name: data.name,
            code: data.code || undefined,
            shipping_rule_ids: selectedRuleIds,
        });
    };

    useEffect(() => {
        if (RESPONSES.updateVendorResponse?.ok) {
            write('subject', 'Vendor ');
            write('other_text', 'has been updated ');
            write('complement', 'successfully!');
            write('bgColor', logMsgBgColors.correct);
            setIsLoading(false);
            setEdit(false);
            setItem(null);
            props.resetList.toggle();
        } else if (RESPONSES.updateVendorResponse?.ok === false) {
            write('subject', 'Vendor');
            write('other_text', 'could not be updated');
            write('complement', 'try again');
            write('bgColor', logMsgBgColors.error);
            setIsLoading(false);
        }
    }, [RESPONSES.updateVendorResponse]);

    useEffect(() => {
        if (RESPONSES.deleteVendorResponse?.ok) {
            write('subject', 'Vendor ');
            write('other_text', 'deleted successfully!');
            write('bgColor', logMsgBgColors.correct);
            props.resetList.toggle();
        } else if (RESPONSES.deleteVendorResponse?.ok === false) {
            write('subject', 'Vendor');
            write('other_text', 'could not be deleted');
            write('bgColor', logMsgBgColors.error);
        }
    }, [RESPONSES.deleteVendorResponse]);

    const renderVendors = vendors.map((vendor, key) => {
        const rules = vendor.shipping_rules ?? vendor.shippingRules ?? [];
        const rulesLabel = rules.length ? rules.map((r: any) => r.name).join(', ') : '-';
        return (
            <div key={key} className='user-card' onClick={() => openEdit(vendor)}>
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    <p style={{ fontFamily: 'Roboto' }}>{vendor.name}</p>
                    {vendor.code && (
                        <p style={{ fontFamily: 'Roboto', fontSize: 12, color: '#b2b3bd', margin: '2px 0 0 0' }}>
                            {vendor.code}
                        </p>
                    )}
                    <p style={{ fontFamily: 'Roboto', fontSize: 12, color: '#b2b3bd', margin: '2px 0 0 0' }}>
                        Policy: {rulesLabel}
                    </p>
                </div>
                <button
                    style={{ width: 50, marginLeft: 10, height: 20, paddingTop: 5, borderRadius: 14 }}
                    className='add-button'
                    onClick={(e) => { e.stopPropagation(); openEdit(vendor); }}
                >
                    <p style={{ fontSize: 12, margin: 0 }} className='add-buttonText'>Edit</p>
                </button>
                <img
                    style={{
                        cursor: 'pointer',
                        marginLeft: 'auto',
                        marginRight: isMobile ? '' : '20px',
                    }}
                    src={`${toIcons}/close.svg`}
                    alt='Delete'
                    onClick={(e) => { e.stopPropagation(); handelOnDeleteClick(vendor.id); }}
                />
            </div>
        );
    });

    return (
        <div
            className='users-table shadow'
            style={{
                width: isMobile ? '345px' : '',
                margin: isMobile ? '35px 16px' : '',
            }}
        >
            {edit && item && (
                <CustomModal setShow={setEdit} title='Modifica vendor' description=''>
                    <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
                        <CustomInput
                            title='Nome'
                            name='name'
                            type='text'
                            register={register}
                            errors={errors}
                            width='100%'
                            reset={reset}
                            noIcon={true}
                            defaultValue={item.name}
                            constraints={{ required: true }}
                        />
                        <CustomInput
                            title='Code'
                            name='code'
                            type='text'
                            register={register}
                            errors={errors}
                            width='100%'
                            reset={reset}
                            noIcon={true}
                            defaultValue={item.code ?? ''}
                        />
                        <p className='field-label' style={{ marginTop: 16, marginBottom: 8 }}>Shipping rules (policy)</p>
                        <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, padding: 8 }}>
                            {shippingRules.map((rule) => (
                                <label key={rule.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer' }}>
                                    <input
                                        type='checkbox'
                                        checked={selectedRuleIds.includes(rule.id)}
                                        onChange={() => toggleRule(rule.id)}
                                    />
                                    <span style={{ fontFamily: 'Roboto', fontSize: 14 }}>{rule.name}</span>
                                </label>
                            ))}
                            {shippingRules.length === 0 && <p style={{ fontFamily: 'Roboto', fontSize: 12, color: '#b2b3bd' }}>Nessuna regola disponibile</p>}
                        </div>
                        {isLoading ? (
                            <Loader />
                        ) : (
                            <button style={{ width: 70, marginTop: 20 }} className='add-button' type='submit'>
                                <p className='add-buttonText'>Salva</p>
                            </button>
                        )}
                    </form>
                </CustomModal>
            )}
            <div
                className='user-table-header'
                style={{
                    justifyContent: isMobile ? 'space-between' : '',
                    margin: '31px 25px',
                }}
            >
                <div className='user-table-header-title-box'>
                    <p className='user-table-header-title'>Vendors</p>
                    <p className='user-table-header-description'>Manage vendors and associate shipping rules (policy)</p>
                </div>
                <button className='user-table-header-button shadow' type='button' onClick={props.toggler.toggle}>
                    <p className='user-table-header-button-text'>Add vendor</p>
                </button>
            </div>
            <div className='user-table-content'>
                <div className='offers-table'>
                    <div className='offers-table-fields'>
                        <div style={{ margin: isMobile ? '0' : '' }}>
                            <p className='field-label'>Name</p>
                        </div>
                        {!isMobile && (
                            <div className='u-mail'>
                                <p className='field-label'></p>
                            </div>
                        )}
                        <div className='u-status' style={{ marginLeft: isMobile ? '135px' : '' }}>
                            <p className='field-label'></p>
                        </div>
                    </div>
                    <div className='divider' style={{ width: '100%', marginTop: '10px' }} />
                    {vendors.length > 0 ? (
                        <div className='users-table-results scroll-y'>{renderVendors}</div>
                    ) : (
                        <Loader />
                    )}
                </div>
            </div>
        </div>
    );
};

export default Vendors;
